import { Box } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { hasInvalidGSM7Characters, sanitizeGSM7Text } from '../../../utility/gsm7Validator';

const VAR_REGEX = /\{([\w_]+)\}/g;

const REMOVE_BTN_HTML = `<span class="var-chip-remove" contenteditable="false"><svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none"><path d="M1.2 1.2L4.8 4.8M4.8 1.2L1.2 4.8" stroke="white" stroke-width="1.2" stroke-linecap="round"/></svg></span>`;

const escapeHtml = (str) =>
    str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\r\n|\r|\n/g, '<br>');

const templateToHtml = (template) => {
    if (!template) return '';
    let html = '';
    let lastIndex = 0;
    let match;
    VAR_REGEX.lastIndex = 0;
    while ((match = VAR_REGEX.exec(template)) !== null) {
        const [full, varName] = match;
        html += escapeHtml(template.slice(lastIndex, match.index));
        html += `<span class="var-chip" contenteditable="false" data-var="${varName}">{${varName}}${REMOVE_BTN_HTML}</span>`;
        lastIndex = match.index + full.length;
    }
    html += escapeHtml(template.slice(lastIndex));
    return html;
};

const domToTemplate = (container) => {
    let result = '';
    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = (node.textContent || '').replace(/\u00A0/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '');
            result += text;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList?.contains('var-chip')) {
                result += `{${node.dataset.var}}`;
            } else if (node.tagName === 'BR') {
                result += '\n';
            } else if (node.tagName === 'DIV' || node.tagName === 'P') {
                if (result.length > 0 && !result.endsWith('\n')) result += '\n';
                node.childNodes.forEach(walk);
            } else {
                node.childNodes.forEach(walk);
            }
        }
    };
    container.childNodes.forEach(walk);
    return result;
};

const findChipAtBoundary = (range, direction) => {
    const { startContainer, startOffset } = range;
    let node = null;

    if (startContainer.nodeType === Node.TEXT_NODE) {
        if (direction === 'before' && startOffset !== 0) return null;
        if (direction === 'after' && startOffset !== startContainer.length) return null;
        node = startContainer;
    } else {
        const childIdx = direction === 'before' ? startOffset - 1 : startOffset;
        const el = startContainer.childNodes[childIdx];
        if (el && el.nodeType === Node.ELEMENT_NODE) {
            return el.classList?.contains('var-chip') ? el : null;
        }
        return null;
    }

    const sibling = direction === 'before' ? node.previousSibling : node.nextSibling;
    return sibling?.nodeType === Node.ELEMENT_NODE && sibling.classList?.contains('var-chip') ? sibling : null;
};

const MessageTemplateEditor = forwardRef(({ value, onChange, placeholder, sx }, ref) => {
    const editorRef = useRef(null);
    const lastKnownValue = useRef(null); // Initialized to null so initial mount ALWAYS populates innerHTML
    const savedRangeRef = useRef(null);

    // Rebuild DOM only when value changed from OUTSIDE typing (initial load / RESET / API fetch)
    useEffect(() => {
        const el = editorRef.current;
        if (!el || value === lastKnownValue.current) return;
        el.innerHTML = templateToHtml(value);
        lastKnownValue.current = value;
        savedRangeRef.current = null;
    }, [value]);

    const emitChange = useCallback(() => {
        const el = editorRef.current;
        if (!el) return;
        const next = domToTemplate(el);
        lastKnownValue.current = next;
        onChange(next);
    }, [onChange]);

    // Continuously track cursor position while this editor is focused
    useEffect(() => {
        const handleSelectionChange = () => {
            const el = editorRef.current;
            if (!el || document.activeElement !== el) return;
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                if (el.contains(range.startContainer)) {
                    savedRangeRef.current = range.cloneRange();
                }
            }
        };
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, []);

    useImperativeHandle(ref, () => ({
        insertVariable(varName) {
            const el = editorRef.current;
            if (!el) return;
            el.focus();

            const sel = window.getSelection();
            let range = savedRangeRef.current;
            if (!range || !el.contains(range.startContainer)) {
                range = document.createRange();
                range.selectNodeContents(el);
                range.collapse(false); // fallback: end of content
            }
            sel.removeAllRanges();
            sel.addRange(range);

            const chip = document.createElement('span');
            chip.className = 'var-chip';
            chip.setAttribute('contenteditable', 'false');
            chip.dataset.var = varName;
            chip.innerHTML = `{${varName}}${REMOVE_BTN_HTML}`;

            range.deleteContents();
            range.insertNode(chip);

            const newRange = document.createRange();
            newRange.setStartAfter(chip);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);

            savedRangeRef.current = newRange.cloneRange();
            emitChange();
        },
        focus() {
            editorRef.current?.focus();
        },
    }));

    const handleClick = (e) => {
        const chip = e.target.closest('.var-chip');
        if (chip) {
            e.preventDefault();
            e.stopPropagation();
            chip.remove();
            emitChange();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key !== 'Backspace' && e.key !== 'Delete') return;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return; // let native selection-delete handle Ctrl+A
        const range = sel.getRangeAt(0);
        const chip = findChipAtBoundary(range, e.key === 'Backspace' ? 'before' : 'after');
        if (chip) {
            e.preventDefault();
            chip.remove();
            emitChange();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData?.getData('text/plain') || '';
        if (!pastedText) return;

        const sanitized = sanitizeGSM7Text(pastedText);

        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();

        const textNode = document.createTextNode(sanitized);
        range.insertNode(textNode);

        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        sel.removeAllRanges();
        sel.addRange(range);

        emitChange();
    };

    const handleBeforeInput = (e) => {
        const inputData = e.data;
        if (!inputData) return;

        const hasSmartPunctuation = /[\u201C\u201D\u2018\u2019\u2013\u2014\u2026]/.test(inputData);
        if (hasSmartPunctuation) {
            e.preventDefault();
            const converted = inputData
                .replace(/[\u201C\u201D]/g, '"')
                .replace(/[\u2018\u2019]/g, "'")
                .replace(/[\u2013\u2014]/g, '-')
                .replace(/\u2026/g, '...');

            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                range.deleteContents();
                const textNode = document.createTextNode(converted);
                range.insertNode(textNode);
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                sel.removeAllRanges();
                sel.addRange(range);
            }
            emitChange();
            return;
        }

        if (hasInvalidGSM7Characters(inputData)) {
            e.preventDefault();
        }
    };

    return (
        <Box
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={emitChange}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBeforeInput={handleBeforeInput}
            data-placeholder={placeholder}
            sx={{
                fontSize: '14px',
                lineHeight: '24px',
                outline: 'none',
                minHeight: '100%',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                '&:empty:before': {
                    content: 'attr(data-placeholder)',
                    color: 'rgba(0,0,0,0.4)',
                },
                '& .var-chip': {
                    position: 'relative',
                    color: '#006CDD',
                    backgroundColor: 'transparent',
                    padding: '1px 2px',
                    margin: '0 0',
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: '1px solid transparent',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: 500,
                    verticalAlign: 'middle',
                    lineHeight: '1.4',
                    '&:hover': {
                        borderRadius: '6px',
                        border: '1px solid rgba(0, 108, 221, 0.08)',
                    },
                    '&:hover .var-chip-remove': {
                        display: 'flex',
                    },
                },
                '& .var-chip-remove': {
                    display: 'none',
                    position: 'absolute',
                    right: '-4px',
                    top: '-4px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: '#006CDD',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 10,
                    boxSizing: 'border-box',
                    padding: 0,
                    margin: 0,
                    lineHeight: 0,
                    fontSize: 0,
                    '& svg': {
                        width: '6px',
                        height: '6px',
                        display: 'block',
                        margin: '0 auto',
                    },
                },
                ...sx,
            }}
        />
    );
});

MessageTemplateEditor.displayName = 'MessageTemplateEditor';

export default MessageTemplateEditor;
